// src/hooks/useVoice.ts - Real-Time Voice Hook with OpenAI Realtime API
import { useState, useCallback, useRef, useEffect } from 'react';
import { VoiceState, VoiceSettings } from '../types/Message';
import { getStoredToken } from '../contexts/AuthContext';

interface VoiceHookReturn {
  voiceState: VoiceState;
  voiceSettings: VoiceSettings;
  updateVoiceSettings: (settings: Partial<VoiceSettings>) => void;
  updateTranscription: (text: string, confidence?: number) => void;
  clearVoiceError: () => void;
  toggleVoice: () => void;
  setVoiceEnabled: (enabled: boolean) => void;
  setRecording: (recording: boolean) => void;
  setProcessing: (processing: boolean) => void;
  setSpeaking: (speaking: boolean) => void;
  setInputMode: (mode: 'text' | 'voice') => void;
  stopAudio: () => void;
  requestMicrophonePermission: () => Promise<boolean>;
  startRecording: () => Promise<boolean>;
  stopRecording: () => void;
  getAudioDevices: () => Promise<MediaDeviceInfo[]>;
  startRealtimeVoice: () => Promise<boolean>;
  stopRealtimeVoice: () => void;
  // Legacy methods for backward compatibility (deprecated)
  sendTextToSpeech: (text: string, emotion?: any) => void;
  uploadAudioFile: (fileData: string, filename: string, mimeType: string) => void;
  sendVoiceMessage: (audioData: string, format: string) => void;
}

// Default voice settings for OpenAI Realtime
const defaultVoiceSettings: VoiceSettings = {
  voice: 'onyx',
  speed: 1.0,
  volume: 0.8,
  pitch: 1.0,
  emotion: 'calm',
  emotionIntensity: 0.5,
  responseFormat: 'pcm_24000',
  quality: 'standard',
  autoPlayResponses: true,
  pushToTalkKey: 'Space',
  language: 'en',
  responseMode: 'auto',
  emotionalVoice: true,
  voiceProfile: 'max',
  streaming: true,
  latencyTarget: 135,
  adaptivePersonality: true,
  voiceEvolution: true
};

export const useVoice = (
  sendMessage?: (message: any) => void,
  sessionId?: string | null
): VoiceHookReturn => {
  // Voice state
  const [voiceState, setVoiceState] = useState<VoiceState>({
    isVoiceEnabled: true,
    isConnected: false,
    isRecording: false,
    isProcessing: false,
    isSpeaking: false,
    lastError: null,
    error: null,
    currentInputMode: 'text',
    currentAudio: null,
    volume: 0.8,
    transcription: '',
    confidence: 0,
    audioLevel: 0,
    noiseLevel: 0,
    speechDetected: false,
    microphonePermission: 'prompt',
    speakerPermission: 'prompt',
    audioDevices: {
      microphones: [],
      speakers: []
    }
  });

  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(defaultVoiceSettings);

  // Refs for real-time voice
  const voiceWsRef = useRef<WebSocket | null>(null);
  // Every scheduled-but-not-finished audio source (interruption stops them all)
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([]);

  // 📱 SCREEN WAKE LOCK (Oct 21 2026 — Ryan: "The phone going dark when on a call will cut it off which makes it
  // hard to use in the car"). While a call is up, the screen stays on; the browser lets go of the lock whenever the
  // page is hidden, so it's taken again when the page comes back.
  const wakeLockRef = useRef<any>(null);
  const holdScreenAwake = useCallback(async () => {
    try {
      const nav: any = navigator;
      if (!nav.wakeLock || wakeLockRef.current || document.visibilityState !== 'visible') return;
      wakeLockRef.current = await nav.wakeLock.request('screen');
      wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null; });
      console.log('📱 Screen kept awake for the call');
    } catch (e) { console.warn('📱 Could not keep the screen awake:', e); }
  }, []);
  const letScreenSleep = useCallback(() => {
    try { wakeLockRef.current?.release?.(); } catch { /* already released */ }
    wakeLockRef.current = null;
  }, []);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && voiceWsRef.current?.readyState === WebSocket.OPEN) holdScreenAwake();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); letScreenSleep(); };
  }, [holdScreenAwake, letScreenSleep]);

  // 🎛️ Live voice re-selection: when the user picks a different voice in the
  // sidebar mid-session, apply it to the active realtime session too.
  useEffect(() => {
    const onVoiceChanged = (e: Event) => {
      const voice = (e as CustomEvent<string>).detail;
      if (voice && voiceWsRef.current?.readyState === WebSocket.OPEN) {
        voiceWsRef.current.send(JSON.stringify({ type: 'set_voice', voice }));
        console.log('🎛️ Live session voice change:', voice);
      }
    };
    window.addEventListener('emcog-tts-voice-changed', onVoiceChanged);
    return () => window.removeEventListener('emcog-tts-voice-changed', onVoiceChanged);
  }, []);
  // 🎚️ Noise suppression is a per-device trade-off: DESKTOP mics with the
  // user seated nearby leave it OFF so the EmCog can hear the world (pot
  // drops, the cat); PHONES keep it ON — speakerphone echo + ambient noise
  // constantly tripped "user is speaking" and halted playback on the
  // slightest sound (hair-trigger interruptions, NUC install 2026-07-09).
  const IS_MOBILE = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const userHasInteracted = useRef<boolean>(false);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);  // 🔥 Track current playing source
  const interruptedRef = useRef<boolean>(false);  // 🔥 Flag to break out of play loop
  // 🔥 FIX: Track scheduled time for gapless playback (prevents crackles/pops)
  const nextScheduledTimeRef = useRef<number>(0);

  // Track user interaction for autoplay restrictions
  useEffect(() => {
    const handleUserInteraction = () => {
      userHasInteracted.current = true;
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // Request microphone permission
  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    try {
      // Check if we're on mobile and not using HTTPS
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSecure = window.location.protocol === 'https:';
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isMobile && !isSecure && !isLocalhost) {
        console.error('❌ Mobile browsers require HTTPS for microphone access');
        setVoiceState(prev => ({
          ...prev,
          microphonePermission: 'denied',
          error: 'Mobile browsers require HTTPS. Please access via https:// or check if microphone is blocked in browser settings.'
        }));
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: IS_MOBILE, // 🎚️ phones: ON (echo/noise = hair-trigger interruptions); desktop: OFF (ambient hearing)
          autoGainControl: true,
          sampleRate: 24000 // OpenAI Realtime uses 24kHz
        }
      });

      setVoiceState(prev => ({
        ...prev,
        microphonePermission: 'granted'
      }));

      // Stop the stream immediately as we only needed permission
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error: any) {
      console.error('Microphone permission denied:', error);

      let errorMessage = 'Microphone permission denied';

      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is already in use by another application.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Microphone access blocked due to security settings. HTTPS may be required.';
      }

      setVoiceState(prev => ({
        ...prev,
        microphonePermission: 'denied',
        error: errorMessage
      }));
      return false;
    }
  }, []);

  // Get available audio devices
  const getAudioDevices = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(device =>
        device.kind === 'audioinput' || device.kind === 'audiooutput'
      );

      const microphones = audioDevices.filter(device => device.kind === 'audioinput');
      const speakers = audioDevices.filter(device => device.kind === 'audiooutput');

      setVoiceState(prev => ({
        ...prev,
        audioDevices: {
          microphones,
          speakers,
          selectedMicrophone: prev.audioDevices?.selectedMicrophone || microphones[0]?.deviceId,
          selectedSpeaker: prev.audioDevices?.selectedSpeaker || speakers[0]?.deviceId
        }
      }));

      return audioDevices;
    } catch (error) {
      console.error('Failed to get audio devices:', error);
      return [];
    }
  }, []);

  // Convert Float32Array to PCM16
  const floatTo16BitPCM = (float32Array: Float32Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return buffer;
  };

  // Convert PCM16 to Float32Array
  const pcm16ToFloat32 = (buffer: ArrayBuffer): Float32Array => {
    const view = new DataView(buffer);
    const floatArray = new Float32Array(buffer.byteLength / 2);
    for (let i = 0; i < floatArray.length; i++) {
      const int16 = view.getInt16(i * 2, true);
      floatArray[i] = int16 < 0 ? int16 / 0x8000 : int16 / 0x7FFF;
    }
    return floatArray;
  };

  // Play audio from queue
  // 🔥 FIXED: Gapless playback - schedule chunks in advance to prevent crackles/pops
  // Also supports interruption by tracking current source and checking interrupted flag
  const playAudioQueue = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
    }

    isPlayingRef.current = true;
    interruptedRef.current = false;  // Reset interrupted flag
    const audioContext = audioContextRef.current;

    // 🔥 Process ALL queued chunks and schedule them back-to-back for gapless playback
    while (audioQueueRef.current.length > 0 && !interruptedRef.current) {
      const audioData = audioQueueRef.current.shift();
      if (!audioData) continue;

      // 🔥 Check for interruption before scheduling next chunk
      if (interruptedRef.current) {
        console.log('⚡ Interruption detected - stopping before next chunk');
        break;
      }

      try {
        const audioBuffer = audioContext.createBuffer(1, audioData.length, 24000);
        audioBuffer.getChannelData(0).set(audioData);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        // 🔥 Track current source so we can stop it on interruption
        currentSourceRef.current = source;
        // 🔥 Track EVERY scheduled source — interruption must stop them all,
        // not just the last-created one (source.start() was already called
        // on each; stopping only currentSourceRef left the rest playing).
        scheduledSourcesRef.current.push(source);
        source.addEventListener('ended', () => {
          scheduledSourcesRef.current = scheduledSourcesRef.current.filter(s => s !== source);
        });

        // 🔥 Schedule chunk at precise time for gapless playback
        const currentTime = audioContext.currentTime;
        const startTime = Math.max(currentTime, nextScheduledTimeRef.current);

        // Calculate when this chunk will end
        const chunkDuration = audioBuffer.duration;
        nextScheduledTimeRef.current = startTime + chunkDuration;

        source.start(startTime);

        // Only set onended callback for the last scheduled chunk in this batch
        if (audioQueueRef.current.length === 0) {
          source.onended = () => {
            // 🔥 CRITICAL ORDER: clear the busy flag BEFORE re-entering
            // playAudioQueue — its first line returns when isPlayingRef is
            // true, so the old order (re-enter first, clear later in the
            // else-branch) made the restart bounce off its own guard: one
            // chunk played, the rest sat queued forever, and isSpeaking
            // never cleared ("split-second reply then stuck Speaking...").
            // The beta API's bursty delivery usually out-raced this; GA's
            // steady pacing hit it every time.
            isPlayingRef.current = false;
            currentSourceRef.current = null;
            if (audioQueueRef.current.length > 0 && !interruptedRef.current) {
              // More chunks arrived while this batch played — keep going,
              // preserving nextScheduledTimeRef for gapless continuation.
              playAudioQueue();
            } else {
              // Reset scheduled time when done
              nextScheduledTimeRef.current = 0;
              setVoiceState(prev => ({ ...prev, isSpeaking: false }));
            }
          };
        }
      } catch (error) {
        console.error('Error playing audio chunk:', error);
      }
    }

    // If interrupted, clean up immediately
    if (interruptedRef.current) {
      isPlayingRef.current = false;
      currentSourceRef.current = null;
      nextScheduledTimeRef.current = 0;
      setVoiceState(prev => ({ ...prev, isSpeaking: false }));
    }
  }, []);

  // Start real-time voice connection
  const startRealtimeVoice = useCallback(async (): Promise<boolean> => {
    try {
      // Request microphone permission
      if (voiceState.microphonePermission !== 'granted') {
        const hasPermission = await requestMicrophonePermission();
        if (!hasPermission) return false;
      }

      // Create WebSocket connection to /voice endpoint
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;

      // Determine the correct port (same logic as consciousness WebSocket)
      const currentPort = window.location.port;

      let wsUrl: string;
      if (currentPort) {
        // Explicit port in URL - use it (development mode)
        wsUrl = `${protocol}//${host}:${currentPort}/voice`;
      } else {
        // No port in URL - backend is serving on same origin (production/ngrok)
        wsUrl = `${protocol}//${host}/voice`;
      }

      // 🔥 FIX: Add auth token to voice WebSocket URL
      const token = getStoredToken();
      if (token) {
        wsUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;
        console.log('🔐 Auth token included in voice WebSocket URL');
      }

      console.log('🎙️ Connecting to voice WebSocket:', wsUrl);
      console.log('   - Protocol:', protocol);
      console.log('   - Host:', host);
      console.log('   - Port:', currentPort || 'none (same origin)');
      console.log('   - Current location:', window.location.href);

      const ws = new WebSocket(wsUrl);
      voiceWsRef.current = ws;

      ws.onopen = async () => {
        console.log('✅ Voice WebSocket connected');
        setVoiceState(prev => ({ ...prev, isConnected: true, error: null }));
        holdScreenAwake();

        // Start audio streaming
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: IS_MOBILE, // 🎚️ see IS_MOBILE note above
            autoGainControl: true,
            sampleRate: 24000,
            deviceId: voiceState.audioDevices?.selectedMicrophone
          }
        });

        streamRef.current = stream;

        // Create audio context and processor
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        processorNodeRef.current = processor;

        let audioChunksSent = 0;
        processor.onaudioprocess = (event) => {
          if (ws.readyState === WebSocket.OPEN) {
            const inputData = event.inputBuffer.getChannelData(0);
            const pcm16 = floatTo16BitPCM(inputData);
            ws.send(pcm16);

            // Log every 100 chunks to verify audio is flowing
            audioChunksSent++;
            if (audioChunksSent % 100 === 0) {
              console.log(`🎤 Sent ${audioChunksSent} audio chunks`);
            }
          }
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        setVoiceState(prev => ({ ...prev, isRecording: true }));
        console.log('🎤 Audio streaming started');
      };

      ws.onmessage = (event) => {
        if (event.data instanceof Blob) {
          // Receive audio from server (legacy format)
          event.data.arrayBuffer().then(buffer => {
            const float32 = pcm16ToFloat32(buffer);
            audioQueueRef.current.push(float32);

            setVoiceState(prev => ({ ...prev, isSpeaking: true }));
            playAudioQueue();
          });
        } else {
          // Receive text messages (transcripts, audio deltas, etc.)
          try {
            const message = JSON.parse(event.data);

            // Handle OpenAI Realtime audio deltas
            if (message.type === 'response.audio.delta') {
              console.log('🔊 Received audio delta from backend');

              // Decode base64 audio
              const base64Audio = message.delta;
              if (base64Audio) {
                // Decode base64 to binary
                const binaryString = atob(base64Audio);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }

                // Convert PCM16 to Float32
                const float32 = pcm16ToFloat32(bytes.buffer);
                audioQueueRef.current.push(float32);

                setVoiceState(prev => ({ ...prev, isSpeaking: true }));
                playAudioQueue();
              }
            }
            // (The spoken conversation's lines reach the chat from the server's kept conversation — on every screen,
            // this one too — so transcripts aren't turned into bubbles here: Oct 28 2026.)
            // Handle legacy transcript format
            else if (message.type === 'transcript') {
              setVoiceState(prev => ({
                ...prev,
                transcription: message.text,
                confidence: message.confidence || 1.0
              }));
            }
            // Handle session ready
            else if (message.type === 'session.ready') {
              console.log('✅ Voice session ready:', message.sessionId);
              // 🎛️ Apply the user's selected voice (same one read-aloud uses)
              // so realtime and TTS speak with one voice. The EmCog can still
              // change it mid-session via its own change_voice agency.
              try {
                const selectedVoice = localStorage.getItem('emcog_tts_voice');
                if (selectedVoice && ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'set_voice', voice: selectedVoice }));
                  console.log('🎛️ Requested session voice:', selectedVoice);
                }
              } catch { /* localStorage unavailable — keep server default */ }
            }
            // 🔥 Handle interruption - stop audio immediately when user interrupts
            else if (message.type === 'interruption.detected') {
              console.log('⚡ INTERRUPTION DETECTED - stopping audio playback immediately!');

              // Set interrupted flag to break out of play loop
              interruptedRef.current = true;

              // Clear the audio queue
              audioQueueRef.current = [];

              // 🔥 CRITICAL: Stop ALL scheduled audio sources — each chunk is
              // its own already-started source; stopping only the last one
              // left the rest of the sentence playing through interruptions.
              for (const source of scheduledSourcesRef.current) {
                try { source.stop(); } catch { /* already ended */ }
              }
              scheduledSourcesRef.current = [];
              currentSourceRef.current = null;
              console.log('⚡ Stopped all scheduled audio sources');

              isPlayingRef.current = false;
              nextScheduledTimeRef.current = 0;  // 🔥 Reset scheduled time

              // Update state
              setVoiceState(prev => ({
                ...prev,
                isSpeaking: false
              }));
            }
          } catch (e) {
            // Ignore non-JSON messages
            console.debug('Non-JSON message received:', e);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('Voice WebSocket error:', error);
        setVoiceState(prev => ({
          ...prev,
          error: 'Voice connection error',
          isConnected: false
        }));
      };

      ws.onclose = () => {
        console.log('🔌 Voice WebSocket closed');
        letScreenSleep();
        setVoiceState(prev => ({
          ...prev,
          isConnected: false,
          isRecording: false,
          isSpeaking: false
        }));
      };

      return true;
    } catch (error) {
      console.error('Failed to start real-time voice:', error);
      setVoiceState(prev => ({
        ...prev,
        error: `Failed to start voice: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
      return false;
    }
  }, [voiceState.microphonePermission, voiceState.audioDevices?.selectedMicrophone, requestMicrophonePermission, playAudioQueue, holdScreenAwake, letScreenSleep]);

  // Stop real-time voice
  const stopRealtimeVoice = useCallback(() => {
    letScreenSleep();
    // Close WebSocket
    if (voiceWsRef.current) {
      voiceWsRef.current.close();
      voiceWsRef.current = null;
    }

    // Stop audio stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Disconnect processor
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current = null;
    }

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextScheduledTimeRef.current = 0;  // 🔥 Reset scheduled time

    setVoiceState(prev => ({
      ...prev,
      isConnected: false,
      isRecording: false,
      isSpeaking: false,
      transcription: ''
    }));

    console.log('🔇 Real-time voice stopped');
  }, [letScreenSleep]);

  // Legacy recording methods (kept for compatibility with old UI)
  const startRecording = useCallback(async (): Promise<boolean> => {
    await startRealtimeVoice();
    return true;
  }, [startRealtimeVoice]);

  const stopRecording = useCallback(() => {
    stopRealtimeVoice();
  }, [stopRealtimeVoice]);

  // Stop audio
  const stopAudio = useCallback(() => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextScheduledTimeRef.current = 0;  // 🔥 Reset scheduled time
    setVoiceState(prev => ({
      ...prev,
      isSpeaking: false,
      currentAudio: null
    }));
  }, []);

  // Update voice settings
  const updateVoiceSettings = useCallback((settings: Partial<VoiceSettings>) => {
    setVoiceSettings(prev => ({
      ...prev,
      ...settings
    }));
  }, []);

  // Update transcription
  const updateTranscription = useCallback((text: string, confidence?: number) => {
    setVoiceState(prev => ({
      ...prev,
      transcription: text,
      confidence: confidence || prev.confidence,
      isProcessing: false
    }));
  }, []);

  // Clear voice error
  const clearVoiceError = useCallback(() => {
    setVoiceState(prev => ({
      ...prev,
      error: null,
      lastError: null
    }));
  }, []);

  // Toggle voice enabled state
  const toggleVoice = useCallback(() => {
    setVoiceState(prev => ({
      ...prev,
      isVoiceEnabled: !prev.isVoiceEnabled
    }));
  }, []);

  // Set voice enabled
  const setVoiceEnabled = useCallback((enabled: boolean) => {
    setVoiceState(prev => ({
      ...prev,
      isVoiceEnabled: enabled
    }));
  }, []);

  // Set recording state
  const setRecording = useCallback((recording: boolean) => {
    if (recording && !voiceState.isRecording) {
      startRecording();
    } else if (!recording && voiceState.isRecording) {
      stopRecording();
    }
  }, [voiceState.isRecording, startRecording, stopRecording]);

  // Set processing state
  const setProcessing = useCallback((processing: boolean) => {
    setVoiceState(prev => ({
      ...prev,
      isProcessing: processing
    }));
  }, []);

  // Set speaking state
  const setSpeaking = useCallback((speaking: boolean) => {
    setVoiceState(prev => ({
      ...prev,
      isSpeaking: speaking
    }));
  }, []);

  // Set input mode
  const setInputMode = useCallback((mode: 'text' | 'voice') => {
    console.log('🔧 useVoice setInputMode called:', mode);
    setVoiceState(prev => ({
      ...prev,
      currentInputMode: mode
    }));
  }, []);

  // 🔥 DEPRECATED: Legacy method for backward compatibility (TTS now handled by Realtime API)
  const sendTextToSpeech = useCallback((text: string, emotion?: any) => {
    console.warn('⚠️ sendTextToSpeech is deprecated - voice is now handled by OpenAI Realtime API');
    // No-op: Voice output is now handled automatically by the Realtime API
  }, []);

  // 🔥 DEPRECATED: Legacy method for backward compatibility (file upload now different)
  const uploadAudioFile = useCallback((fileData: string, filename: string, mimeType: string) => {
    console.warn('⚠️ uploadAudioFile is deprecated - use real-time voice instead');
    // No-op: Real-time voice doesn't use file uploads
  }, []);

  // 🔥 DEPRECATED: Legacy method for backward compatibility (voice messages now real-time)
  const sendVoiceMessage = useCallback((audioData: string, format: string) => {
    console.warn('⚠️ sendVoiceMessage is deprecated - use real-time voice instead');
    // No-op: Voice is now duplex real-time streaming
  }, []);

  // NOTE: The old spacebar push-to-talk keyboard shortcut was removed —
  // voice mode is now a continuous realtime session and server-side VAD
  // handles turn-taking. A spacebar handler here would have torn the live
  // session down on the first keypress.

  // Initialize audio devices on mount
  useEffect(() => {
    if (voiceState.isVoiceEnabled) {
      getAudioDevices();
    }
  }, [voiceState.isVoiceEnabled, getAudioDevices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRealtimeVoice();

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRealtimeVoice]);

  return {
    voiceState,
    voiceSettings,
    updateVoiceSettings,
    updateTranscription,
    clearVoiceError,
    toggleVoice,
    setVoiceEnabled,
    setRecording,
    setProcessing,
    setSpeaking,
    setInputMode,
    stopAudio,
    requestMicrophonePermission,
    startRecording,
    stopRecording,
    getAudioDevices,
    startRealtimeVoice,
    stopRealtimeVoice,
    // Legacy methods (deprecated)
    sendTextToSpeech,
    uploadAudioFile,
    sendVoiceMessage
  };
};
